import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { createInstance, Identify } from '@amplitude/analytics-browser';
import {
  type GetCurrentUserAndOrganizationForAmplitudeQuery,
  type GetCurrentUserAndOrganizationForAmplitudeQueryVariables,
} from '../generated/graphql';
import { useAuthContext } from './AuthContext';
import { useIframeRenderData } from './IframeRenderDataContext';
import { useLimitTrackingMobile } from '../hooks/useLimitTrackingMobile';

type EventProperties = Record<string, unknown>;

export type McpUiAmplitudeEventName =
  | 'user-agent-location-selected'
  | 'booked-suggestion'
  | 'book-all'
  | 'book-all-multi-day'
  | 'viewed-suggested-spaces';

type AmplitudeContextValue = {
  trackEvent: (eventName: McpUiAmplitudeEventName, eventProperties?: EventProperties) => void;
};

interface AmplitudeRenderData {
  ampDeviceId?: string;
  ampSessionId?: number;
}

const AmplitudeContext = createContext<AmplitudeContextValue>({
  trackEvent: () => undefined,
});

const APPLICATION_NAME = 'agent';
const amplitudeClient = createInstance();
let isAmplitudeInitialized = false;

const GET_CURRENT_USER_AND_ORGANIZATION_FOR_AMPLITUDE = gql`
  query GetCurrentUserAndOrganizationForAmplitude {
    getCurrentUserAndOrgs {
      user {
        id
      }
      organizationForCurrentTenant {
        id
        slug
        name
      }
    }
  }
`;

export const AmplitudeProvider = ({ children }: { children: ReactNode }) => {
  const { tenantId, authToken } = useAuthContext();
  const renderData = useIframeRenderData<AmplitudeRenderData>();

  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  const ampDeviceId = renderData?.ampDeviceId;
  const ampSessionId = renderData?.ampSessionId;

  const isEmbeddedInIframe = window.parent !== window;
  const hasIframeAmplitudeSessionState = Boolean(ampDeviceId) && ampSessionId != null;
  const hasIframeRenderData = Boolean(authToken) && hasIframeAmplitudeSessionState;

  const { data, loading } = useQuery<
    GetCurrentUserAndOrganizationForAmplitudeQuery,
    GetCurrentUserAndOrganizationForAmplitudeQueryVariables
  >(GET_CURRENT_USER_AND_ORGANIZATION_FOR_AMPLITUDE, {
    skip: !tenantId,
  });

  const user = data?.getCurrentUserAndOrgs?.user ?? null;
  const organization = data?.getCurrentUserAndOrgs?.organizationForCurrentTenant ?? null;

  const { limitTracking, loading: limitTrackingLoading } = useLimitTrackingMobile(user?.id);

  const baseEventProperties = useMemo(
    () => ({
      application: APPLICATION_NAME,
    }),
    []
  );

  useEffect(() => {
    // Wait until the user's tracking preference is resolved, then never
    // initialize Amplitude when tracking is limited (matches the mobile app,
    // which never starts Amplitude for a limited user).
    if (!apiKey || isAmplitudeInitialized || loading || limitTrackingLoading || limitTracking) {
      return;
    }

    if (isEmbeddedInIframe && !hasIframeRenderData) {
      return;
    }

    amplitudeClient.init(apiKey, {
      autocapture: false,
      defaultTracking: false,
      deviceId: ampDeviceId,
      sessionId: ampSessionId,
    });
    isAmplitudeInitialized = true;
  }, [
    apiKey,
    ampDeviceId,
    ampSessionId,
    hasIframeRenderData,
    isEmbeddedInIframe,
    loading,
    limitTrackingLoading,
    limitTracking,
  ]);

  useEffect(() => {
    if (!apiKey || loading || limitTrackingLoading || limitTracking) {
      return;
    }

    if (user?.id) {
      amplitudeClient.setUserId(user.id);
    }

    if (organization?.id) {
      amplitudeClient.setGroup('organizationId', organization.id);
      const identify = new Identify();

      if (organization.slug) {
        identify.set('organizationSlug', organization.slug);
      }
      if (organization.name) {
        identify.set('organizationName', organization.name);
      }

      amplitudeClient.identify(identify);
    }
  }, [
    apiKey,
    loading,
    limitTrackingLoading,
    limitTracking,
    organization?.id,
    organization?.name,
    organization?.slug,
    user?.id,
  ]);

  const trackEvent = useCallback(
    (eventName: McpUiAmplitudeEventName, eventProperties?: EventProperties) => {
      // No-op when tracking is limited so no events are sent for this user.
      // Also wait until the preference is resolved so events don't slip through
      // while limitTracking still holds its default (false) during loading.
      if (!apiKey || limitTrackingLoading || limitTracking) {
        return;
      }

      amplitudeClient.track(eventName, {
        ...baseEventProperties,
        ...eventProperties,
      });
    },
    [apiKey, baseEventProperties, limitTrackingLoading, limitTracking]
  );

  return <AmplitudeContext.Provider value={{ trackEvent }}>{children}</AmplitudeContext.Provider>;
};

export const useAmplitude = () => useContext(AmplitudeContext);
